import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../services/api';
import { notify } from 'messages';
import { flattenQuery } from 'helpers/flattenQuery';
import { FilterConfig } from 'hooks/useListQuery';

export interface Integration {
  integration_id: number;
  name: string;
  description?: string | null;
  zone_id: number;
  zone_name: string;
  folder_id?: number | null;
  folder_name?: string | null;
  base_url?: string | null;
  connection_id?: number | null;
  auth_basic: boolean;
  auth_bearer: boolean;
  auth_oauth2: boolean;
  created_at?: string;
  updated_at?: string;
}

interface PaginationState {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

interface IntegrationsState {
  integrations: Integration[];
  pagination: PaginationState;
  status: string | null;
  error: string | null;
  auditTrail: any;
  config: { header_key: string; label: string; header_type: string }[];
  filtersConfig: FilterConfig[];
  loading: {
    getIntegrations: boolean;
    getIntegrationsPaginated: boolean;
    createIntegration: boolean;
    updateIntegration: boolean;
    deleteIntegration: boolean;
    getIntegrationAuditTrail: boolean;
  };
}

const initialState: IntegrationsState = {
  integrations: [],
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 2000,
  },
  status: null,
  error: null,
  auditTrail: null,
  config: [],
  filtersConfig: [],
  loading: {
    getIntegrations: false,
    getIntegrationsPaginated: false,
    createIntegration: false,
    updateIntegration: false,
    deleteIntegration: false,
    getIntegrationAuditTrail: false,
  },
};

// Fetch Integrations (non-paginated, for dropdowns)
export const getIntegrations = createAsyncThunk<{
  integrations: Integration[];
}>('integrations/getIntegrations', async () => {
  const response = await api.get('/integrations', {
    params: { limit: initialState.pagination.pageSize },
  });
  return response.data;
});

// Fetch Integrations (paginated, for list page)
export const getIntegrationsPaginated = createAsyncThunk('integrations/getIntegrationsPaginated', async (params: Record<string, any>) => {
  const queryParams = {
    limit: initialState.pagination.pageSize,
    ...params,
  };
  const response = await api.get('/integrations', {
    params: flattenQuery(queryParams),
  });
  return response.data;
});

// Create Integration
export const createIntegration = createAsyncThunk<
  Integration,
  {
    name: string;
    description?: string;
    zone_id?: number;
    zone_name?: string;
    base_url?: string;
    connection_id?: number | null;
    auth_basic?: boolean;
    auth_bearer?: boolean;
    auth_oauth2?: boolean;
    parent_folder_id?: number | null;
  },
  { rejectValue: { message: string; error?: string } }
>('integrations/createIntegration', async (data, { rejectWithValue }) => {
  try {
    const response = await api.post('/integrations', data);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: err.message });
  }
});

// Update Integration
export const updateIntegration = createAsyncThunk<
  Integration,
  {
    integration_id: number;
    name?: string;
    description?: string;
    zone_id?: number;
    zone_name?: string;
    base_url?: string;
    connection_id?: number | null;
    auth_basic?: boolean;
    auth_bearer?: boolean;
    auth_oauth2?: boolean;
  },
  { rejectValue: { message: string; error?: string } }
>('integrations/updateIntegration', async (data, { rejectWithValue }) => {
  try {
    const response = await api.put(`/integrations/${data.integration_id}`, data);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: err.message });
  }
});

// Delete Integration
export const deleteIntegration = createAsyncThunk<{ message: string }, number, { rejectValue: { message: string; error?: string } }>(
  'integrations/deleteIntegration',
  async (integrationId, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/integrations/${integrationId}`);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

// Get Integration Audit Trail
export const getIntegrationAuditTrail = createAsyncThunk<any, number>('integrations/getIntegrationAuditTrail', async (integrationId) => {
  const response = await api.get(`/integrations/audit_trail/${integrationId}`);
  return response.data;
});

const integrationsSlice = createSlice({
  name: 'integrations',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ================== getIntegrations ==================
      .addCase(getIntegrations.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.getIntegrations = true;
      })
      .addCase(getIntegrations.fulfilled, (state, action) => {
        state.integrations = action.payload.integrations;
        state.status = 'succeeded';
        state.loading.getIntegrations = false;
      })
      .addCase(getIntegrations.rejected, (state, action) => {
        state.status = 'failed';
        state.loading.getIntegrations = false;
        state.error = action.error.message || 'Failed to fetch integrations';
      });

    builder
      .addCase(getIntegrationsPaginated.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.getIntegrationsPaginated = true;
      })
      .addCase(getIntegrationsPaginated.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const { integrations, config, filtersConfig, pagination } = action.payload;

        state.integrations = integrations;
        state.config = config;
        state.filtersConfig = filtersConfig || [];

        state.pagination.totalItems = pagination?.total_records ?? 0;
        state.pagination.totalPages = pagination?.total_pages ?? 0;
        state.pagination.pageSize = pagination?.limit ?? state.pagination.pageSize;
        state.pagination.currentPage = (pagination?.current_page ?? 1) > (pagination?.total_pages ?? 0) ? 1 : (pagination?.current_page ?? 1);

        state.loading.getIntegrationsPaginated = false;
      })
      .addCase(getIntegrationsPaginated.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch integrations';
        state.loading.getIntegrationsPaginated = false;
      });

    builder
      // ================== createIntegration ==================
      .addCase(createIntegration.pending, (state) => {
        state.loading.createIntegration = true;
        state.error = null;
      })
      .addCase(createIntegration.fulfilled, (state, action) => {
        state.integrations.push(action.payload);
        state.status = 'succeeded';
        state.loading.createIntegration = false;

        const messageKey = 'data-created-integration';
        if (!notify.isShowing(messageKey)) {
          notify.success('Integration created successfully!', { key: messageKey });
        }
      })
      .addCase(createIntegration.rejected, (state, action) => {
        state.loading.createIntegration = false;
        state.status = 'failed';

        const payload = action.payload as { message?: string; error?: string } | undefined;

        state.error = payload?.error || payload?.message || 'Failed to create integration';

        const messageKey = 'data-failed-to-create-integration';
        if (!notify.isShowing(messageKey)) {
          notify.error(`Failed to create new integration! ${state.error}`, {
            key: messageKey,
          });
        }
      })

      // ================== updateIntegration ==================
      .addCase(updateIntegration.pending, (state) => {
        state.loading.updateIntegration = true;
        state.error = null;
      })
      .addCase(updateIntegration.fulfilled, (state, action) => {
        const index = state.integrations.findIndex((i) => i.integration_id === action.payload.integration_id);
        if (index !== -1) {
          state.integrations[index] = action.payload;
        }
        state.status = 'succeeded';
        state.loading.updateIntegration = false;

        const messageKey = 'data-updated-integration';
        if (!notify.isShowing(messageKey)) {
          notify.success('Integration updated successfully!', { key: messageKey });
        }
      })
      .addCase(updateIntegration.rejected, (state, action) => {
        state.loading.updateIntegration = false;
        state.status = 'failed';

        const payload = action.payload as { message?: string; error?: string } | undefined;

        state.error = payload?.error || payload?.message || 'Failed to update integration';

        const messageKey = 'data-failed-to-update-integration';
        if (!notify.isShowing(messageKey)) {
          notify.error(`Failed to update the integration. Message: ${state.error}`, {
            key: messageKey,
          });
        }
      })

      // ================== deleteIntegration ==================
      .addCase(deleteIntegration.pending, (state) => {
        state.loading.deleteIntegration = true;
        state.error = null;
      })
      .addCase(deleteIntegration.fulfilled, (state, action) => {
        state.integrations = state.integrations.filter((i) => i.integration_id !== action.meta.arg);
        state.status = 'succeeded';
        state.loading.deleteIntegration = false;

        const messageKey = 'data-deleted-integration';
        if (!notify.isShowing(messageKey)) {
          notify.success('Integration deleted successfully!', { key: messageKey });
        }
      })
      .addCase(deleteIntegration.rejected, (state, action) => {
        state.loading.deleteIntegration = false;
        state.status = 'failed';

        const payload = action.payload as { message?: string; error?: string } | undefined;

        state.error = payload?.error || payload?.message || 'Failed to delete integration';

        const messageKey = 'data-failed-to-delete-integration';
        if (!notify.isShowing(messageKey)) {
          notify.error(`Failed to delete the integration! ${state.error}`, {
            key: messageKey,
          });
        }
      })

      // ================== getIntegrationAuditTrail ==================
      .addCase(getIntegrationAuditTrail.pending, (state) => {
        state.loading.getIntegrationAuditTrail = true;
      })
      .addCase(getIntegrationAuditTrail.fulfilled, (state, action) => {
        state.loading.getIntegrationAuditTrail = false;
        state.status = 'succeeded';
        state.auditTrail = action.payload;
      })
      .addCase(getIntegrationAuditTrail.rejected, (state, action) => {
        state.loading.getIntegrationAuditTrail = false;
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch integration audit trail';
      });
  },
});

export default integrationsSlice.reducer;
